/**
 * useFlowchartStartPicker — owns the per-type Start picker state + handlers
 * for a freshly-created flowchart (Blank / Basic / Swimlane / BPMN / Generate).
 * Extracted from the editor shell so App.tsx stays orchestration-only.
 *
 * Seeds run through the production stack via `runSeedById`
 * (RDM → ApplyRdmSceneCommand). After content lands we call `onSeeded` so the
 * shell can zoom-to-fit; `onOpenGenerate` opens the assistant's generate mode.
 */

import { useCallback, useState } from 'react';
import type { CommandManager } from '../../../core/commands/CommandManager';
import { runSeedById } from '../../../core/seeds/runSeed';

export interface UseFlowchartStartPickerOptions {
  commandManager: CommandManager;
  /** Fires after a starter seeds content, so the shell can zoom-to-fit. */
  onSeeded: () => void;
  /** Opens the assistant generate-from-text mode. */
  onOpenGenerate: () => void;
}

export interface FlowchartStartPickerState {
  /** True once the user has picked any starter — hides the picker overlay. */
  chosen: boolean;
  onStartBlank: () => void;
  onGenerateFromText: () => void;
  onSelectStarter: (seedId: string) => Promise<void>;
}

export function useFlowchartStartPicker({
  commandManager,
  onSeeded,
  onOpenGenerate,
}: UseFlowchartStartPickerOptions): FlowchartStartPickerState {
  const [chosen, setChosen] = useState(false);

  const onStartBlank = useCallback(() => setChosen(true), []);

  const onGenerateFromText = useCallback(() => {
    setChosen(true);
    onOpenGenerate();
  }, [onOpenGenerate]);

  const onSelectStarter = useCallback(
    async (seedId: string): Promise<void> => {
      setChosen(true);
      try {
        const seeded = await runSeedById(commandManager, seedId);
        if (seeded) onSeeded();
      } catch (error) {
        console.warn('[flowchartStarter] failed to seed', seedId, error);
      }
    },
    [commandManager, onSeeded],
  );

  return { chosen, onStartBlank, onGenerateFromText, onSelectStarter };
}
